/**
 * Frontend-API-Client für /api/users.
 * Nutzt React-Query-kompatible Funktionen.
 */

import { apiFetch } from './api';
import type { AuthUser, CompanyRole, EmploymentType, LocationRole, SalaryMode, UserRole } from '../store/auth';

export type ApiUser = AuthUser;

/**
 * Phase A (Kapitel 20.3): ersetzt das alte, optionale `allowedLocationIds`
 * ("leer = überall erlaubt"). Mind. 1 Zuordnung ist Pflicht — der Server lehnt
 * ein leeres Array ab. `role` fehlt/`undefined` → Server nimmt `employee` an;
 * Filialleiter dürfen hier ohnehin nur `employee` einsetzen (serverseitig
 * erzwungen).
 */
export interface LocationAssignmentInput {
  locationId: string;
  role?: LocationRole;
  isPrimary?: boolean;
}

export interface CreateUserInput {
  email: string;
  name: string;
  /** Kapitel 3.2: optional — leer = Erstanmeldung mit dem Filialpasswort des Haupt-Standorts. */
  password?: string;
  locationAssignments: LocationAssignmentInput[];
  /** Nur von Owner/Admin ausgewertet — macht den User zusätzlich zum Firmen-Owner. */
  companyRole?: CompanyRole;
  /** Nur von einem globalen Admin/Superadmin ausgewertet (System-Rolle, nicht Standort-/Firmen-Rolle). */
  role?: UserRole;
  hourlyWage: number;
  breakDeductionEnabled?: boolean;
  billingCycleStartDay?: number;
  salaryMode?: SalaryMode;
  monthlySalary?: number;
  monthlyTargetHours?: number;
  employmentType?: EmploymentType;
  /** Nachtrag 11.09.2026: nur vom Chef ausgewertet. */
  canTakeDailyPay?: boolean;
  /** v1.4.0: Einladungs-Mail mit Link zum eigenen Passwort schicken. */
  sendInvite?: boolean;
}

/** Ergebnis des Mailversands: verschickt / kein Mailserver eingerichtet / Fehler. */
export type InviteResult = 'sent' | 'not-configured' | 'failed';

export interface UpdateUserInput {
  email?: string;
  name?: string;
  password?: string;
  role?: UserRole;
  hourlyWage?: number;
  breakDeductionEnabled?: boolean;
  billingCycleStartDay?: number;
  salaryMode?: SalaryMode;
  monthlySalary?: number;
  monthlyTargetHours?: number;
  employmentType?: EmploymentType;
  /** Wenn angegeben: ersetzt die Standort-Zuordnungen dieses Users INNERHALB der aktiven Firma. */
  locationAssignments?: LocationAssignmentInput[];
  companyRole?: CompanyRole;
  canTakeDailyPay?: boolean;
}

export const usersApi = {
  list: (token: string) =>
    apiFetch<{ users: ApiUser[] }>('/api/users', { token }).then((r) => r.users),

  create: (token: string, data: CreateUserInput | Record<string, unknown>) =>
    apiFetch<{ user: ApiUser; invite?: InviteResult }>('/api/users', { method: 'POST', body: data, token }),

  /** v1.4.0: Einladung (erneut) schicken — nur bei offener Erstanmeldung. */
  invite: (token: string, id: string) =>
    apiFetch<{ invite: InviteResult }>(`/api/users/${id}/invite`, { method: 'POST', token }).then((r) => r.invite),

  update: (token: string, id: string, data: UpdateUserInput | Record<string, unknown>) =>
    apiFetch<{ user: ApiUser }>(`/api/users/${id}`, { method: 'PUT', body: data, token }).then(
      (r) => r.user,
    ),

  remove: (token: string, id: string) =>
    apiFetch<void>(`/api/users/${id}`, { method: 'DELETE', token }),

  // Phase U+Z (Kapitel 20.3, Entscheidung #30/#35) — Deaktivieren statt Löschen.
  futureShiftCount: (token: string, id: string) =>
    apiFetch<{ count: number }>(`/api/users/${id}/future-shift-count`, { token }).then(
      (r) => r.count,
    ),

  deactivate: (token: string, id: string) =>
    apiFetch<{ user: ApiUser }>(`/api/users/${id}/deactivate`, { method: 'POST', token }).then(
      (r) => r.user,
    ),

  reactivate: (token: string, id: string) =>
    apiFetch<{ user: ApiUser }>(`/api/users/${id}/reactivate`, { method: 'POST', token }).then(
      (r) => r.user,
    ),
};
